import { Button } from '@repo/ui/components/button';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import {
  describePage,
  hasNextPage,
  hasPreviousPage,
  pageAfter,
  type PageState,
} from './pagination';

/**
 * The control under a paged list: where you are, and one step either way.
 *
 * ## Why this is not the registry's `pagination`
 *
 * `base-luma` does publish one, and the rule in CLAUDE.md is to take what the
 * registry gives rather than hand-write it. This is the exception and it is
 * worth stating, because "the registry has one" is the first thing anyone will
 * check.
 *
 * That component is built for URL-driven paging: `PaginationLink` hardcodes
 * `render={<a />}`, so every control is an anchor. These lists hold their
 * offset in component state — no ticket asks for a linkable page and adding
 * `?offset=` to five screens being built in parallel is routing surface nobody
 * asked for — which would leave each control an `href="#"` with a
 * `preventDefault` on it. A link that goes nowhere is worse for a keyboard and
 * a screen reader than the button this actually is. It also carries no
 * arithmetic whatsoever, so `pagination.ts` would exist either way.
 *
 * If a linkable page is ever wanted, that is the moment to reach for the
 * registry component — the decision reverses cleanly, which is why the sums
 * live in a separate file that knows nothing about either.
 *
 * ## Numbered pages
 *
 * Deliberately absent. A price history is read from the top and a catalogue is
 * filtered rather than jumped around in, so page numbers would be chrome
 * nobody uses — and they are the part of a pager that needs an ellipsis
 * algorithm and a decision about how many to show. Previous, next and a count
 * is the whole of what these screens need.
 */
export function Pager({
  state,
  onOffsetChange,
  label,
}: {
  readonly state: PageState;

  onOffsetChange: (offset: number) => void;

  /**
   * What is being paged, for the navigation's accessible name — `"prices"`,
   * `"catalogue items"`. Two paged lists on one screen are otherwise two
   * landmarks called "pagination".
   */
  readonly label: string;
}) {
  // Nothing to page through. The list's own empty state is already saying so,
  // and a disabled pager under it would be a second, quieter voice on the same
  // subject.
  if (state.total <= state.limit) return null;

  return (
    <nav aria-label={`${label} pages`} className="flex items-center justify-between gap-4 pt-2">
      {/* `polite`, so moving a page announces where you landed rather than
          interrupting. The count is the only thing that changes. */}
      <p aria-live="polite" className="text-sm text-muted-foreground">
        {describePage(state)}
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!hasPreviousPage(state)}
          onClick={() => onOffsetChange(pageAfter(state, -1))}
        >
          <IconChevronLeft data-icon="inline-start" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasNextPage(state)}
          onClick={() => onOffsetChange(pageAfter(state, 1))}
        >
          Next
          <IconChevronRight data-icon="inline-end" />
        </Button>
      </div>
    </nav>
  );
}
